import { useState, useCallback } from 'react';
import api from '../api';

/**
 * useTrustHubValidation Hook
 *
 * Validates Trust Hub profile status before campaign creation
 * Returns validation function and modal state
 *
 * Usage:
 * const { validateTrustHub, showValidationModal, setShowValidationModal, profileStatus } = useTrustHubValidation(user_id);
 *
 * // In your campaign save handler:
 * const isValid = await validateTrustHub();
 * if (!isValid) {
 *   return; // Modal will show automatically
 * }
 * // Continue with campaign creation...
 */
const useTrustHubValidation = (userId) => {
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [validating, setValidating] = useState(false);

  /**
   * Validates Trust Hub profile status
   * @returns {Promise<boolean>} true if profile is approved, false otherwise
   */
  const validateTrustHub = useCallback(async () => {
    if (!userId) {
      console.warn('useTrustHubValidation: No userId provided');
      return false;
    }

    setValidating(true);

    try {
      const response = await api.trustHubCentralized.getProfileStatus(userId);

      if (!response.success) {
        console.error('Failed to fetch Trust Hub profile status:', response.message);
        // No profile exists - show modal
        setProfileStatus(null);
        setShowValidationModal(true);
        return false;
      }

      const status = response.data?.status;
      setProfileStatus(status);

      // Check if profile is approved
      if (status === 'twilio-approved') {
        return true; // Valid - can proceed with campaign creation
      }

      // Not approved - show validation modal
      setShowValidationModal(true);
      return false;
    } catch (error) {
      console.error('Error validating Trust Hub profile:', error);
      // On error, show modal to be safe
      setProfileStatus(null);
      setShowValidationModal(true);
      return false;
    } finally {
      setValidating(false);
    }
  }, [userId]);

  return {
    validateTrustHub,
    showValidationModal,
    setShowValidationModal,
    profileStatus,
    validating
  };
};

export default useTrustHubValidation;
